/**
 * Music Player component
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 3.8
 */

/**
 * MusicPlayer - Handles audio playback with autoplay on user interaction
 * 
 * Requirements:
 * - 1.1: Start playing music automatically when visitor interacts with page
 * - 1.2: Hide play button when music starts automatically
 * - 1.3: Only trigger autoplay once per page load
 * - 1.4: Do not display play/pause button by default
 * - 1.5: Show minimal play button if browser blocks autoplay
 * - 1.6: Play configured background music on main page
 * - 3.8: Play simulation music on simulation page
 */
export class MusicPlayer {
  private audio: HTMLAudioElement | null = null;
  private button: HTMLButtonElement | null = null;
  private isPlaying = false;
  private hasTriggered = false;  // Prevent multiple autoplay triggers (Req 1.3)
  private interactionHandler: ((e: Event) => void) | null = null;

  /**
   * Mount the music player to a container
   * Button is hidden by default (Req 1.4), shown only if autoplay fails (Req 1.5)
   */
  mount(container: HTMLElement, musicUrl: string): void {
    this.audio = new Audio(musicUrl);
    this.audio.loop = true;
    
    // Create enable button (hidden by default per Req 1.4)
    this.button = document.createElement('button');
    this.button.textContent = '🔇 Включить звук';
    this.button.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      padding: 12px 20px;
      background-color: rgba(255, 255, 255, 0.1);
      color: #FFFFFF;
      border: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 8px;
      cursor: pointer;
      font-size: 14px;
      z-index: 99999;
      transition: background-color 0.2s;
      display: none;
    `;
    
    this.button.addEventListener('mouseenter', () => {
      if (this.button) {
        this.button.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
      }
    });
    
    this.button.addEventListener('mouseleave', () => {
      if (this.button) {
        this.button.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
      }
    });
    
    this.button.addEventListener('click', (e) => {
      e.stopPropagation(); // Don't trigger the document click handler
      this.togglePlay();
    });
    
    container.appendChild(this.button);
    
    // Try autoplay immediately (works on first visit)
    // If blocked, set up listeners for user interaction (Req 1.1)
    this.tryAutoplayOrWaitForInteraction();
  }

  /**
   * Try autoplay immediately, if blocked wait for user interaction
   */
  private async tryAutoplayOrWaitForInteraction(): Promise<void> {
    if (!this.audio || this.hasTriggered) return;
    
    try {
      await this.audio.play();
      this.isPlaying = true;
      this.hasTriggered = true;
      // Button stays hidden when autoplay succeeds (Req 1.2)
      this.updateButton();
    } catch (error) {
      // Autoplay blocked - wait for user interaction
      console.log('Autoplay blocked on load, waiting for user interaction');
      this.interactionHandler = () => this.tryAutoplayOnce();
      document.addEventListener('click', this.interactionHandler, { capture: true });
      document.addEventListener('pointerdown', this.interactionHandler, { capture: true });
      document.addEventListener('keydown', this.interactionHandler, { capture: true });
      document.addEventListener('mousemove', this.interactionHandler, { once: true });
    }
  }

  /**
   * Try autoplay once on first user interaction (Req 1.3)
   */
  private tryAutoplayOnce(): void {
    if (this.hasTriggered) return;
    this.hasTriggered = true;
    
    // Remove all listeners after first trigger
    this.removeInteractionListeners();
    
    this.tryAutoplay();
  }

  /**
   * Remove all interaction listeners
   */
  private removeInteractionListeners(): void {
    if (this.interactionHandler) {
      document.removeEventListener('click', this.interactionHandler, { capture: true });
      document.removeEventListener('pointerdown', this.interactionHandler, { capture: true });
      document.removeEventListener('keydown', this.interactionHandler, { capture: true });
      document.removeEventListener('mousemove', this.interactionHandler);
      this.interactionHandler = null;
    }
  }

  /**
   * Attempt to autoplay music (Req 1.1, 1.2, 1.5)
   */
  private async tryAutoplay(): Promise<void> {
    if (!this.audio) return;
    
    try {
      await this.audio.play();
      this.isPlaying = true;
      // Button stays hidden when autoplay succeeds (Req 1.2)
      this.updateButton();
    } catch (error) {
      // Autoplay blocked - show minimal button (Req 1.5)
      console.log('Autoplay blocked, showing enable button');
      if (this.button) {
        this.button.style.display = 'block';
      }
      this.updateButton();
    }
  }

  /**
   * Toggle play/pause
   */
  togglePlay(): void {
    if (!this.audio) return;
    
    if (this.isPlaying) {
      this.audio.pause();
      this.isPlaying = false;
    } else {
      this.audio.play().then(() => {
        this.isPlaying = true;
        this.updateButton();
      }).catch(console.error);
    }
    
    this.updateButton();
  }

  /**
   * Update button text based on state
   */
  private updateButton(): void {
    if (!this.button) return;
    
    if (this.isPlaying) {
      this.button.textContent = '🔊 Выключить звук';
    } else {
      this.button.textContent = '🔇 Включить звук';
    }
  }

  /**
   * Play music
   */
  play(): void {
    if (this.audio && !this.isPlaying) {
      this.audio.play().then(() => {
        this.isPlaying = true;
        this.updateButton();
      }).catch(console.error);
    }
  }

  /**
   * Stop music
   */
  stop(): void {
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
      this.isPlaying = false;
    }
  }

  /**
   * Set music URL
   */
  setSource(url: string): void {
    if (this.audio) {
      const wasPlaying = this.isPlaying;
      this.stop();
      this.audio.src = url;
      if (wasPlaying) {
        this.play();
      }
    }
  }

  /**
   * Clean up
   */
  destroy(): void {
    // Remove interaction listeners if still attached
    this.removeInteractionListeners();
    this.stop();
    if (this.button) {
      this.button.remove();
      this.button = null;
    }
    this.audio = null;
    this.hasTriggered = false;
  }

  /**
   * Check if autoplay has been triggered (for testing)
   */
  getHasTriggered(): boolean {
    return this.hasTriggered;
  }
}
